import {memo, useState} from 'react';
import Link from 'next/link';
import type {NavCategory, NavLink} from '@/types/navigation';
import Icon from '@/components/atoms/Icon';
import styles from './PopNavMenu.module.scss';

const cx = (...classes: (string | false | undefined | null)[]) => classes.filter(Boolean).join(' ');

// ─── Card-style category panel ────────────────────────────────────────────────
// Each category renders as a card: an orange marker + heading, then its links. Cards show
// at most CARD_VISIBLE_ROWS links (the rest stay in the HTML, hidden until "Explore more"),
// and every card in a row is held to the tallest one's height so the headings line up.
const CARD_ROW_HEIGHT = 24;
const CARD_ROW_GAP = 8;
const CARD_LIST_PADDING = 5;
// 4px/4px header padding + one 14px line at lineHeight 1.3, held at 29px by its min-height.
const CARD_HEADER_HEIGHT = 29;
const CARD_VISIBLE_ROWS = 6;
export const CARD_COLUMNS = 3;
const CATEGORY_GAP = 4;
// One 16px label line + 12px bottom margin for the "Explore more" button.
const EXPLORE_BUTTON_BLOCK = 16 + 12;

// Height of a list showing `rows` complete rows (top padding only — see cardListOuterHeight).
const cardListHeight = (rows: number) => CARD_LIST_PADDING + rows * CARD_ROW_HEIGHT + (rows - 1) * CARD_ROW_GAP;
// The space the rendered list actually takes in the card, bottom padding included.
const cardListOuterHeight = (rows: number) => cardListHeight(rows) + CARD_LIST_PADDING;

const rowHeights = (columns: NavCategory[][]): number[] => {
    const rowCount = Math.max(0, ...columns.map((column) => column?.length ?? 0));

    return Array.from({length: rowCount}, (_, rowIndex) => {
        const linkCounts = columns.map((column) => column?.[rowIndex]?.links?.length ?? 0).filter((count) => count > 0);
        if (!linkCounts.length) return 0;
        const rows = Math.min(CARD_VISIBLE_ROWS, Math.max(...linkCounts));
        const hasOverflow = linkCounts.some((count) => count > CARD_VISIBLE_ROWS);
        return CARD_HEADER_HEIGHT + cardListOuterHeight(rows) + (hasOverflow ? EXPLORE_BUTTON_BLOCK : 0);
    });
};

export const distributeIntoColumns = (categories: NavCategory[], columnCount: number): NavCategory[][] => {
    const columns: NavCategory[][] = Array.from({length: columnCount}, () => []);
    categories.forEach((category, index) => columns[index % columnCount].push(category));
    return columns;
};

interface CategoryCardProps {
    category: NavCategory;
    asPath: string;
    minHeight: number;
    onLinkClick: (index?: number) => void;
}

const CategoryCard = memo(({category, asPath, minHeight, onLinkClick}: CategoryCardProps) => {
    const [isExpanded, setIsExpanded] = useState(false);

    const links = category.links ?? [];
    const hiddenCount = links.length - CARD_VISIBLE_ROWS;

    return (
        <div className={`nav-category-card ${styles.card}`} style={{minHeight: `${minHeight}px`}}>
            <div className={styles.cardHeader}>
                <div aria-hidden="true" className={styles.cardDot} />
                <h3 className={styles.cardTitle}>{category.categoryHeading}</h3>
            </div>
            <div className={cx(styles.cardList, (isExpanded || hiddenCount <= 0) && styles.cardListUncapped)}>
                {links.map((link: NavLink, linkIndex: number) => (
                    <Link
                        key={linkIndex}
                        href={link.path}
                        target={link.target || ''}
                        className="menu-links"
                        onClick={() => onLinkClick(link?.number)}
                    >
                        <div className={cx(styles.cardLink, asPath === link.path && styles.cardLinkActive)}>{link.label}</div>
                    </Link>
                ))}
            </div>
            {hiddenCount > 0 && (
                <button
                    type="button"
                    className={styles.exploreButton}
                    aria-expanded={isExpanded}
                    aria-label={
                        isExpanded ? `Show fewer ${category.categoryHeading} services` : `Explore more ${category.categoryHeading} services`
                    }
                    onClick={() => setIsExpanded((open) => !open)}
                >
                    <span className={styles.exploreLabel}>{isExpanded ? 'Show less' : 'Explore more'}</span>
                    <Icon
                        name="ArrowRightAltRounded"
                        fontSize="inherit"
                        className={cx(styles.exploreGlyph, isExpanded && styles.exploreGlyphExpanded)}
                    />
                </button>
            )}
        </div>
    );
});

CategoryCard.displayName = 'CategoryCard';

export const renderCategoryColumns = (columns: NavCategory[][], heading: string, asPath: string, onLinkClick: (n?: number) => void) => {
    const heights = rowHeights(columns);
    const totalHeight = heights.reduce((total, height) => total + height, 0) + Math.max(0, heights.length - 1) * CATEGORY_GAP;

    return columns.map((column: NavCategory[], columnIndex: number) => (
        <div key={columnIndex} className={styles.categoryColumn} style={{minHeight: `${totalHeight}px`}}>
            {column.map((category: NavCategory, catIndex: number) => (
                <CategoryCard
                    key={`${heading}-${columnIndex}-${catIndex}`}
                    category={category}
                    asPath={asPath}
                    minHeight={heights[catIndex]}
                    onLinkClick={onLinkClick}
                />
            ))}
        </div>
    ));
};
